import { useCallback, useEffect, useState } from 'react';
import {
  fetchWorkerSession,
  getApiBaseUrl,
  isApiConfigured,
  probeWorkerConnectivity,
  toApiError,
  type WorkerConnectivityResult,
} from '@/services/api';
import { useAuth } from '@/shared/hooks/useAuth';

export function WorkerApiStatus() {
  const { firebaseUser } = useAuth();
  const [result, setResult] = useState<WorkerConnectivityResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [authProbe, setAuthProbe] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const [authLoading, setAuthLoading] = useState(false);

  const runPublicProbe = useCallback(async () => {
    setLoading(true);
    try {
      setResult(await probeWorkerConnectivity());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void runPublicProbe();
  }, [runPublicProbe]);

  const runAuthProbe = useCallback(async () => {
    if (!firebaseUser) {
      setAuthProbe({ ok: false, message: 'Sin sesión activa' });
      return;
    }
    setAuthLoading(true);
    setAuthProbe(null);
    try {
      const data = await fetchWorkerSession(true);
      setAuthProbe({
        ok: true,
        message: `Sesión Worker — rol ${data.role}${data.active ? '' : ' (inactivo)'}, uid ${data.uid.slice(0, 8)}…`,
      });
    } catch (err) {
      const apiErr = toApiError(err);
      setAuthProbe({
        ok: false,
        message: apiErr.workerError ?? apiErr.message,
      });
    } finally {
      setAuthLoading(false);
    }
  }, [firebaseUser]);

  const baseUrl = getApiBaseUrl();

  return (
    <div className="inventory-card inventory-card--static worker-api-status">
      <strong>API Worker (erp-worker)</strong>
      <p className="auth-muted">
        Base:{' '}
        {isApiConfigured() ? (
          <code>{baseUrl}</code>
        ) : (
          <span>No configurada (VITE_API_BASE_URL)</span>
        )}
      </p>

      {loading ? <p className="auth-muted">Probando /health y /version…</p> : null}

      {result ? (
        <ul className="worker-api-status__list auth-muted">
          <li>
            /health:{' '}
            {result.health ? (
              <span className="auth-success">
                {result.health.status} — {result.health.environment}
              </span>
            ) : (
              <span className="auth-error">{result.healthError}</span>
            )}
          </li>
          <li>
            /version:{' '}
            {result.version ? (
              <span className="auth-success">
                {result.version.name} v{result.version.version}
              </span>
            ) : (
              <span className="auth-error">{result.versionError}</span>
            )}
          </li>
        </ul>
      ) : null}

      <div className="worker-api-status__actions">
        <button
          type="button"
          className="auth-button auth-button--secondary"
          disabled={loading}
          onClick={() => void runPublicProbe()}
        >
          Probar endpoints públicos
        </button>
        <button
          type="button"
          className="auth-button auth-button--secondary"
          disabled={authLoading || !firebaseUser}
          onClick={() => void runAuthProbe()}
        >
          Probar Bearer (Firebase)
        </button>
      </div>

      {authProbe ? (
        <p className={authProbe.ok ? 'auth-success' : 'auth-error'}>
          {authProbe.message}
        </p>
      ) : null}
      <p className="auth-muted worker-api-status__hint">
        /auth/session verifica JWT (JWKS) y devuelve el rol desde Firestore en
        el Worker, no desde el frontend.
      </p>
    </div>
  );
}
